/**
 * useVideoEngineV2 — React hook for the v2 demo.
 *
 * Drives the backend /api/v2-demo/run endpoint and exposes a typed
 * state for the demo page. NO connection to Content Bot, Weekly
 * Planner, Run My Week, or My Projects. Existing screens are untouched.
 *
 * Phase 2 contract:
 *  - Founder-only (backend enforces is_internal)
 *  - Stub providers only
 *  - Returns the same shape the real engine will return in Phase 4,
 *    so the consuming UI doesn't change when we flip to real providers
 */
import { useCallback, useState } from 'react';
import api from '../utils/api';

const initialState = {
  status: 'idle', // idle | running | success | failed
  result: null,   // backend response shape
  error: null,
};

export const useVideoEngineV2 = () => {
  const [state, setState] = useState(initialState);

  const run = useCallback(async (params) => {
    setState({ status: 'running', result: null, error: null });
    try {
      const resp = await api.post('/v2-demo/run', params);
      const data = resp?.data;
      setState({
        status: data?.ok ? 'success' : 'failed',
        result: data,
        error: data?.ok ? null : (data?.error_message || data?.fail_reason || 'Unknown failure'),
      });
      return data;
    } catch (err) {
      const detail = err?.response?.data?.detail;
      const msg = typeof detail === 'string'
        ? detail
        : (detail?.message || err?.message || 'Demo run failed');
      setState({ status: 'failed', result: null, error: msg });
      throw err;
    }
  }, []);

  const reset = useCallback(() => setState(initialState), []);

  return { ...state, run, reset };
};
